import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mdiLoading, mdiRotateLeft, mdiUploadMultiple } from '@mdi/js';
import Icon from '@mdi/react';
import iziToast from 'izitoast';
import React, { FormEvent, useContext } from 'react';
import { AppAgentContext } from 'src/context/ContextProvider';
import { Assets, uploadFileCompressedService } from 'src/services/AssetService';
import { appActions, DataStates, State } from 'src/store';
import { ModalType } from 'src/types/Modal';
import { generateRandomNumber } from 'src/utils';
import { AssetsValidate } from './Modals/AssetsAgentModal';
import { useTranslate } from 'sbx-crm-menu-ui';

interface Props {
    files: Assets[];
    setFiles: React.Dispatch<React.SetStateAction<Assets[]>>;
    setUploadImages: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DragAndDropImages = ({ files, setFiles, setUploadImages }: Props) => {
    const [images, setImages] = React.useState<string[]>([]);
    const [imagesFile, setImagesFile] = React.useState<File[]>([]);
    const [loading] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const inputFileRef = React.useRef<HTMLInputElement>(null);
    const linkAdressRef = React.useRef<HTMLInputElement>(null);
    const {
        state: {
            session: {
                ai_agent_config: { folder_key_avatars = '' },
            },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { t: c } = useTranslate('common');

    const onImages = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const files: File[] = [];
            const filesUrl: string[] = [];
            const fileList = e.target.files;

            for (let i = 0; i < fileList.length; i++) {
                files.push(fileList[i]);
                filesUrl.push(URL.createObjectURL(fileList[i]));
            }

            setImages(filesUrl);
            setImagesFile(files);
        }
    };

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (imagesFile.length === 0) return;
        dispatch(
            appActions.openModal({
                type: ModalType.CONFIRMATION,
                data: {
                    title: 'Subir Imagenes',
                    description: '¿Quieres subir las imagenes?',
                    onClose: () => dispatch(appActions.closeModal(ModalType.CONFIRMATION)),
                    state: DataStates.AGENT_ASSET,
                    onConfirm: async () => {
                        dispatch(appActions.onLoading({ type: DataStates.AGENT_ASSET, status: State.LOADING }));
                        const res = await uploadFileCompressedService(folder_key_avatars, imagesFile);
                        if (res.success) {
                            const combineFiles = [...res.items, ...files];
                            iziToast.success({
                                message: 'Imagenes Subidas Correctamente',
                                position: 'topCenter',
                            });
                            setImagesFile([]);
                            setImages([]);
                            setUploadImages(false);
                            setTimeout(() => {
                                setFiles(combineFiles);
                            }, 2000);
                            dispatch(appActions.onLoading({ type: DataStates.AGENT_ASSET, status: State.SUCCESS }));
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                        } else {
                            dispatch(appActions.onLoading({ type: DataStates.AGENT_ASSET, status: State.ERROR }));
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                        }
                    },
                },
            })
        );
    };

    const selectFiles = () => {
        if (inputFileRef.current) {
            inputFileRef.current.click();
        }
    };

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
        e.dataTransfer.dropEffect = 'copy';
    };

    const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        const typeValid = ['image/png', 'image/jpg', 'image/jpeg'];
        e.preventDefault();
        setIsDragging(false);
        const fileList = e.dataTransfer.files;
        const files: File[] = [];
        const filesUrl: string[] = [];

        for (let i = 0; i < fileList.length; i++) {
            if (typeValid.includes(fileList[i].type)) {
                files.push(fileList[i]);
                filesUrl.push(URL.createObjectURL(fileList[i]));
            }
        }

        setImages(filesUrl);
        setImagesFile(files);
    };

    const deleteImage = (index: number) => {
        setImages((prevImages) => prevImages.filter((_, i) => i !== index));
        setImagesFile((prevImages) => prevImages.filter((_, i) => i !== index));
    };

    function addLinkAddress(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const linkAddress = linkAdressRef.current?.value;
        if (linkAddress) {
            const asset: AssetsValidate = {
                key: generateRandomNumber(7).toString(),
                name: linkAddress.split('/').pop() || `image_${generateRandomNumber(7)}`,
                mime_type: 'image/jpeg',
                mimetype: 'image/jpeg',
                size: 0,
                url: linkAddress,
                isUrl: true,
            }
            setFiles((prevImages) => [...prevImages, asset]);
            setUploadImages(false);
        }
    }

    return (
        <div>
            <form onSubmit={addLinkAddress} className="flex items-center mb-3">
                <input
                    ref={linkAdressRef}
                    className="w-full rounded h-11 p-2 outline-none border border-gray-200"
                    type="text"
                    placeholder={c('type_your_url') + '...'}
                />
                <button className="btn btn-primary min-w-[fit-content] ml-1">{c('add_url')}</button>
            </form>
            <div
                className="border-2 border-dashed border-gray-300 w-full h-[200px] rounded-md flex justify-center items-center"
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                {isDragging ? (
                    <p>{c('drop_files_here')}</p>
                ) : (
                    <>
                        {c('drag_and_drop_files')} {c('or')}{' '}
                        <span className="ml-[6px] text-gray-300" role="button" onClick={selectFiles}>
                            {c('browse')}
                        </span>
                    </>
                )}
            </div>
            <div className="flex gap-2 my-3">
                {images.map((img, index) => (
                    <div key={index} className="w-[100px] h-[100px] relative">
                        <span
                            className="absolute top-1 right-1 cursor-pointer bg-red-500 rounded-full w-5 h-5 flex justify-center items-center"
                            onClick={() => deleteImage(index)}
                        >
                            <FontAwesomeIcon icon={faTimes} color="white" />
                        </span>
                        <img src={img} alt={img} className="w-full h-full object-cover" />
                    </div>
                ))}
            </div>
            <form
                id={'form_images'}
                onReset={() => {
                    setImagesFile([]);
                    setImages([]);
                }}
                onSubmit={onSubmit}
                className="flex justify-between py-2"
            >
                <input
                    disabled={loading}
                    accept="image/*"
                    type="file"
                    className="w-full hidden"
                    onChange={onImages}
                    multiple
                    ref={inputFileRef}
                />
                <div className="flex items-center">
                    <button
                        disabled={!imagesFile.length || loading}
                        type="submit"
                        className={'btn btn-sm mx-1 cursor-pointer disabled:opacity-10 disabled:hover:bg-zinc-700'}
                    >
                        <Icon size={0.8} path={loading ? mdiLoading : mdiUploadMultiple} spin={loading} />
                    </button>
                    {imagesFile.length > 0 && (
                        <button disabled={loading} type="reset" className={'btn btn-sm'}>
                            <Icon size={0.8} path={mdiRotateLeft} />
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};
